import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../api/client';
import '../components/chef/ValidationInterface.css';

const GUIDE = {
  "Évaluation de la totalité des compétences du cours": {
    Excellent: "L'ensemble des évaluations sommatives couvre la totalité des éléments de compétences visées...",
    Bien: "L'ensemble des évaluations sommatives couvre plusieurs éléments de compétences, mais de manière incomplète...",
    Insuffisant: "Seulement une partie limitée est évaluée, laissant des lacunes.",
  },
  "Distribution équilibrée des évaluations sommatives": {
    Excellent: "Charge de travail équilibrée et réaliste, temps de réalisation réaliste.",
    Bien: "Parfois déséquilibrée; temps parfois irréaliste.",
    Insuffisant: "Mauvaise répartition compromettant l'apprentissage.",
  },
  "Présence d'évaluations formatives en classe": {
    Excellent: "Formatives régulières et bien conçues; respect du temps pratique.",
    Bien: "Présentes mais peu variées/espacées.",
    Insuffisant: "Peu ou pas d'évaluations formatives, ou mal alignées.",
  },
  "Présence de lectures ou de travaux hors classe": {
    Excellent: "Bien intégrés et utiles.",
    Bien: "Présents mais limités.",
    Insuffisant: "Absents ou sans lien clair.",
  },
  "Emploi d'approches issues de la pédagogie active": {
    Excellent: "Utilisation variée et cohérente de méthodes actives.",
    Bien: "Utilisation occasionnelle; engagement partiel.",
    Insuffisant: "Peu ou pas de méthodes actives.",
  },
  "Approches adaptées à la matière du cours": {
    Excellent: "Parfaitement alignées avec la matière.",
    Bien: "Généralement adaptées, parfois moyennement pertinentes.",
    Insuffisant: "Peu adaptées ou trop générales.",
  },
  "Alignement des stratégies d'enseignement avec le niveau taxonomique prescrit": {
    Excellent: "Parfaitement alignées.",
    Bien: "Alignement partiel.",
    Insuffisant: "Absence d'alignement.",
  },
  "Intégration des technologies éducatives": {
    Excellent: "Utilisation pertinente des outils.",
    Bien: "Utilisation limitée, impact modéré.",
    Insuffisant: "Peu ou pas d'utilisation.",
  },
  "Concordance de l'évaluation certificative avec le niveau taxonomique du cours": {
    Excellent: "Bien alignée avec le niveau taxonomique.",
    Bien: "Alignement partiel.",
    Insuffisant: "Non alignée, centrée sur des compétences de base.",
  },
  "Préparation à l'évaluation certificative": {
    Excellent: "Progression claire et graduelle.",
    Bien: "Progression adéquate avec quelques écarts.",
    Insuffisant: "Absence de progression claire.",
  },
  "Caractère authentique et complexe de l'évaluation certificative": {
    Excellent: "Situations authentiques et complexes.",
    Bien: "Complexité modérée, peu réaliste.",
    Insuffisant: "Simpliste, faible lien au contexte.",
  },
};

const GuideCell = ({ critere, selectedLevel }) => {
  const guide = GUIDE[critere] || {};
  return (
    <td className="guide-cell">
      <p className="guide-item" data-level="Excellent" data-active={selectedLevel === 'Excellent'}>
        <strong>Excellent :</strong> {guide.Excellent || ''}
      </p>
      <p className="guide-item" data-level="Bien" data-active={selectedLevel === 'Bien'}>
        <strong>Bien :</strong> {guide.Bien || ''}
      </p>
      <p className="guide-item" data-level="Insuffisant" data-active={selectedLevel === 'Insuffisant'}>
        <strong>Insuffisant :</strong> {guide.Insuffisant || ''}
      </p>
    </td>
  );
};

export default function GridView() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [gridData, setGridData] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [validationDate, setValidationDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadGridData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await apiFetch(`/api/plans/${planId}/grille-data`, {
          auth: true,
        });
        if (!response.ok) {
          if (response.status === 404) {
            setError('La grille de validation n\'a pas encore été générée pour ce plan.');
          } else {
            setError('Erreur lors du chargement de la grille.');
          }
          return;
        }
        const data = await response.json();
        setGridData(data.payload);
        setPlanData(data.plan);
        setValidationDate(data.date_validation);
      } catch (err) {
        setError('Impossible de charger la grille de validation.');
      } finally {
        setLoading(false);
      }
    };

    if (planId) {
      loadGridData();
    }
  }, [planId]);

  // Extract rubric data (for the qualitative section)
  const rubric = gridData?.rubric || {};
  const infos = gridData?.infos || {};
  const identification = gridData?.identification || {};
  const presentation = gridData?.presentation || {};
  const competences = gridData?.competences || {};
  const responsabilites = gridData?.responsabilites || {};
  const calendrier = gridData?.calendrier || {};
  const evaluation_apprentissages = gridData?.evaluation_apprentissages || {};
  const politiques = gridData?.politiques || {};
  const ressources = gridData?.ressources || {};
  const epreuve = gridData?.epreuve || {};
  const conformite = gridData?.conformite || {};

  // Extract rubric levels for GuideCell
  const levels = {};
  if (rubric.strategies) {
    levels['Emploi d\'approches issues de la pédagogie active'] = rubric.strategies.pedagogie_active?.level;
    levels['Approches adaptées à la matière du cours'] = rubric.strategies.adaptation_discipline?.level;
    levels['Alignement des stratégies d\'enseignement avec le niveau taxonomique prescrit'] = rubric.strategies.alignement_taxonomique?.level;
    levels['Intégration des technologies éducatives'] = rubric.strategies.techno_educatives?.level;
  }
  if (rubric.calendrier) {
    levels['Présence d\'évaluations formatives en classe'] = rubric.calendrier.formatives_classe?.level;
    levels['Présence de lectures ou de travaux hors classe'] = rubric.calendrier.hors_classe?.level;
  }
  if (rubric.eval_apprentissages) {
    levels['Évaluation de la totalité des compétences du cours'] = rubric.eval_apprentissages.totalite_competences?.level;
    levels['Distribution équilibrée des évaluations sommatives'] = rubric.eval_apprentissages.distribution_equilibree?.level;
  }
  if (rubric.epreuve) {
    levels['Concordance de l\'évaluation certificative avec le niveau taxonomique du cours'] = rubric.epreuve.concordance_taxonomique?.level;
    levels['Préparation à l\'évaluation certificative'] = rubric.epreuve.preparation?.level;
    levels['Caractère authentique et complexe de l\'évaluation certificative'] = rubric.epreuve.authentique_complexe?.level;
  }

  if (loading) return <div className="grid-view-loading">Chargement de la grille...</div>;
  if (error) return <div className="grid-view-error">{error}</div>;

  return (
    <section className="validation-interface">
      {/* Read-only banner */}
      <div className="ro-banner" style={{
        background: '#fff3cd',
        border: '1px solid #ffc107',
        padding: '12px 20px',
        marginBottom: '20px',
        borderRadius: '4px',
        color: '#856404',
        fontWeight: 'bold',
        textAlign: 'center'
      }}>
        📖 Mode lecture seule - Cette grille a été validée et ne peut plus être modifiée
      </div>

      <header className="validation-header">
        <div className="plan-header-info">
          <h1>Grille de validation (Lecture seule)</h1>
          <div className="plan-meta">
            <span className="plan-id">Plan #{planData?.id}</span>
            <span className="plan-course">{planData?.numero_cours} - {planData?.titre}</span>
            <span className="plan-teacher">{planData?.enseignant_nom}</span>
            {validationDate && (
              <span className="plan-date">Validé le: {new Date(validationDate).toLocaleDateString()}</span>
            )}
          </div>
        </div>
        <button className="btn-back" onClick={() => navigate(-1)} style={{
          padding: '10px 20px',
          background: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}>
          ← Retour
        </button>
      </header>

      <form className="grid-form">
        <h2>Informations générales</h2>
        <label>
          Numéro de cours
          <input type="text" defaultValue={infos.numero_cours || ''} disabled />
        </label>
        <label>
          Vérificateur
          <input type="text" defaultValue={infos.verificateur || ''} disabled />
        </label>
        <label>
          Date
          <input type="date" defaultValue={infos.date || ''} disabled />
        </label>

        <h2>Conformité</h2>
        <div className="radio-group">
          <p><strong>Conformité du plan de cours (Chef d'équipe)</strong></p>
          <label><input type="radio" checked={conformite.chef_equipe === 'oui'} disabled /> Oui</label>
          <label><input type="radio" checked={conformite.chef_equipe === 'non'} disabled /> Non</label>
        </div>
        <div className="radio-group">
          <p><strong>Conformité Chef d'équipe (Conseiller pédagogique)</strong></p>
          <label><input type="radio" checked={conformite.conseiller_pedagogique === 'oui'} disabled /> Oui</label>
          <label><input type="radio" checked={conformite.conseiller_pedagogique === 'non'} disabled /> Non</label>
        </div>
        <div className="radio-group">
          <p><strong>Validation par échantillonnage (Directeur adjoint aux études)</strong></p>
          <label><input type="radio" checked={conformite.directeur_adjoint === 'oui'} disabled /> Oui</label>
          <label><input type="radio" checked={conformite.directeur_adjoint === 'non'} disabled /> Non</label>
        </div>

        <h2>Éléments du plan de cours</h2>
        <h3>1. Page d'identification (conformité avec plan-cadre*)</h3>
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td><strong>Numéro du cours</strong><span className="hint">Vérifier si c'est le bon numéro de cours</span></td><td><input type="checkbox" checked={identification.numero_cours || false} disabled /></td><td><textarea defaultValue={identification.numero_cours_comment || ''} disabled /></td></tr>
            <tr><td><strong>Titre du cours</strong><span className="hint">Vérifier si c'est le bon titre du cours</span></td><td><input type="checkbox" checked={identification.titre_cours || false} disabled /></td><td><textarea defaultValue={identification.titre_cours_comment || ''} disabled /></td></tr>
            <tr><td>Programme</td><td><input type="checkbox" checked={identification.programme || false} disabled /></td><td><textarea defaultValue={identification.programme_comment || ''} disabled /></td></tr>
            <tr><td>Discipline</td><td><input type="checkbox" checked={identification.discipline || false} disabled /></td><td><textarea defaultValue={identification.discipline_comment || ''} disabled /></td></tr>
            <tr><td><strong>Pondération (T-L-P)</strong><span className="hint">Est-ce la même que celle indiquée dans la grille de cours ?</span></td><td><input type="checkbox" checked={identification.ponderation || false} disabled /></td><td><textarea defaultValue={identification.ponderation_comment || ''} disabled /></td></tr>
            <tr><td><strong>Préalables ou concomitants</strong><span className="hint">Vérifier dans la grille de cours ou demander à la CP</span></td><td><input type="checkbox" checked={identification.prealables || false} disabled /></td><td><textarea defaultValue={identification.prealables_comment || ''} disabled /></td></tr>
            <tr><td><strong>Cours substituts</strong><span className="hint">Vérifier dans la grille de cours ou demander à la CP</span></td><td><input type="checkbox" checked={identification.substituts || false} disabled /></td><td><textarea defaultValue={identification.substituts_comment || ''} disabled /></td></tr>
            <tr><td>Session</td><td><input type="checkbox" checked={identification.session || false} disabled /></td><td><textarea defaultValue={identification.session_comment || ''} disabled /></td></tr>
            <tr><td>Local</td><td><input type="checkbox" checked={identification.local || false} disabled /></td><td><textarea defaultValue={identification.local_comment || ''} disabled /></td></tr>
            <tr><td>Type de formation</td><td><input type="checkbox" checked={identification.type_formation || false} disabled /></td><td><textarea defaultValue={identification.type_formation_comment || ''} disabled /></td></tr>
            <tr><td>Charge de travail hebdomadaire</td><td><input type="checkbox" checked={identification.charge_travail || false} disabled /></td><td><textarea defaultValue={identification.charge_travail_comment || ''} disabled /></td></tr>
            <tr><td>Nom de l'enseignant.e</td><td><input type="checkbox" checked={identification.nom_enseignant || false} disabled /></td><td><textarea defaultValue={identification.nom_enseignant_comment || ''} disabled /></td></tr>
            <tr><td>Coordonnées de l'enseignant.e</td><td><input type="checkbox" checked={identification.coord_enseignant || false} disabled /></td><td><textarea defaultValue={identification.coord_enseignant_comment || ''} disabled /></td></tr>
            <tr><td>Disponibilités de l'enseignant.e</td><td><input type="checkbox" checked={identification.disponibilites || false} disabled /></td><td><textarea defaultValue={identification.disponibilites_comment || ''} disabled /></td></tr>
            <tr><td>Nom et coordonnées de l'API</td><td><input type="checkbox" checked={identification.api || false} disabled /></td><td><textarea defaultValue={identification.api_comment || ''} disabled /></td></tr>
            <tr><td>Phrase clé du département/de la discipline</td><td><input type="checkbox" checked={identification.phrase_cle || false} disabled /></td><td><textarea defaultValue={identification.phrase_cle_comment || ''} disabled /></td></tr>
          </tbody>
        </table>

        <h3>2. Présentation du cours (conformité avec plan-cadre*)</h3>
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td><strong>Description du cours</strong><span className="hint">Est-ce la description officielle du cours (plan-cadre*) ?</span></td><td><input type="checkbox" checked={presentation.description || false} disabled /></td><td><textarea defaultValue={presentation.description_comment || ''} disabled /></td></tr>
            <tr><td>Place du cours dans le programme</td><td><input type="checkbox" checked={presentation.place_programme || false} disabled /></td><td><textarea defaultValue={presentation.place_programme_comment || ''} disabled /></td></tr>
            <tr><td>Réinvestissement des compétences</td><td><input type="checkbox" checked={presentation.reinvestissement || false} disabled /></td><td><textarea defaultValue={presentation.reinvestissement_comment || ''} disabled /></td></tr>
          </tbody>
        </table>

        <h3>3. Compétences et attitudes du cours</h3>
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td><strong>Nom.s de la/des compétence.s</strong><span className="hint">Est-ce la bonne compétence (devis ministériel) ?</span></td><td><input type="checkbox" checked={competences.noms || false} disabled /></td><td><textarea defaultValue={competences.noms_comment || ''} disabled /></td></tr>
            <tr><td><strong>Élément.s de la/des compétence.s</strong><span className="hint">Sont-ce les bons éléments de compétence du devis ?</span></td><td><input type="checkbox" checked={competences.elements || false} disabled /></td><td><textarea defaultValue={competences.elements_comment || ''} disabled /></td></tr>
            <tr><td><strong>Nom.s de l'/des attitude.s visée.s (savoir-être)</strong><span className="hint">Attitudes visées pertinentes ?</span></td><td><input type="checkbox" checked={competences.attitudes || false} disabled /></td><td><textarea defaultValue={competences.attitudes_comment || ''} disabled /></td></tr>
          </tbody>
        </table>

        <h3>4. Responsabilités de l'étudiant</h3>
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td>Responsabilités de l'étudiant</td><td><input type="checkbox" checked={responsabilites.etudiant || false} disabled /></td><td><textarea defaultValue={responsabilites.etudiant_comment || ''} disabled /></td></tr>
          </tbody>
        </table>

        <h3>5. Calendrier des activités d'apprentissage, d'enseignement et d'évaluation</h3>
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td><strong>Dates des cours</strong><span className="hint">15 semaines ? jours fériés/journées pédagogiques pris en compte ?</span></td><td><input type="checkbox" checked={calendrier.dates_cours || false} disabled /></td><td><textarea defaultValue={calendrier.dates_cours_comment || ''} disabled /></td></tr>
            <tr><td>Contenus des cours (principaux thèmes abordés)</td><td><input type="checkbox" checked={calendrier.contenus_cours || false} disabled /></td><td><textarea defaultValue={calendrier.contenus_cours_comment || ''} disabled /></td></tr>
            <tr><td>Évaluations sommatives avec pondération</td><td><input type="checkbox" checked={calendrier.evaluations_sommatives || false} disabled /></td><td><textarea defaultValue={calendrier.evaluations_sommatives_comment || ''} disabled /></td></tr>
          </tbody>
        </table>

        <h3>6. Évaluation des apprentissages</h3>
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td><strong>Type d'évaluation (examen, projet, présentation orale, etc.)</strong><span className="hint">Diversité des types ?</span></td><td><input type="checkbox" checked={evaluation_apprentissages.types || false} disabled /></td><td><textarea defaultValue={evaluation_apprentissages.types_comment || ''} disabled /></td></tr>
            <tr><td><strong>Éléments de compétence</strong><span className="hint">Tous les éléments couverts durant la session ?</span></td><td><input type="checkbox" checked={evaluation_apprentissages.elements_couverts || false} disabled /></td><td><textarea defaultValue={evaluation_apprentissages.elements_couverts_comment || ''} disabled /></td></tr>
            <tr><td>Critères d'évaluation</td><td><input type="checkbox" checked={evaluation_apprentissages.criteres || false} disabled /></td><td><textarea defaultValue={evaluation_apprentissages.criteres_comment || ''} disabled /></td></tr>
            <tr><td>Échéancier</td><td><input type="checkbox" checked={evaluation_apprentissages.echeancier || false} disabled /></td><td><textarea defaultValue={evaluation_apprentissages.echeancier_comment || ''} disabled /></td></tr>
            <tr><td><strong>Pondération (100%)</strong><span className="hint">Est-ce que le total fait 100% ?</span></td><td><input type="checkbox" checked={evaluation_apprentissages.ponderation_100 || false} disabled /></td><td><textarea defaultValue={evaluation_apprentissages.ponderation_100_comment || ''} disabled /></td></tr>
            <tr><td>L'épreuve certificative compte minimum pour 40%</td><td><input type="checkbox" checked={evaluation_apprentissages.certificative_40 || false} disabled /></td><td><textarea defaultValue={evaluation_apprentissages.certificative_40_comment || ''} disabled /></td></tr>
          </tbody>
        </table>

        <h3>7. Politiques</h3>
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td>PIEA abrégée présente dans le gabarit</td><td><input type="checkbox" checked={politiques.piea_abregee || false} disabled /></td><td><textarea defaultValue={politiques.piea_abregee_comment || ''} disabled /></td></tr>
            <tr><td>Deux évaluations sommatives en plus de la certificative</td><td><input type="checkbox" checked={politiques.deux_sommatives || false} disabled /></td><td><textarea defaultValue={politiques.deux_sommatives_comment || ''} disabled /></td></tr>
            <tr><td>Politiques de l'Institut Teccart</td><td><input type="checkbox" checked={politiques.teccart_harcelement || false} disabled /></td><td><textarea defaultValue={politiques.teccart_harcelement_comment || ''} disabled /></td></tr>
            <tr><td>Décision application art. 4.9.2 (IA) de la PIEA</td><td><input type="checkbox" checked={politiques.article_492_ia || false} disabled /></td><td><textarea defaultValue={politiques.article_492_ia_comment || ''} disabled /></td></tr>
            <tr><td>Règles du cours</td><td><input type="checkbox" checked={politiques.regles_du_cours || false} disabled /></td><td><textarea defaultValue={politiques.regles_du_cours_comment || ''} disabled /></td></tr>
          </tbody>
        </table>

        <h3>8. Ressources du cours</h3>
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td>Liste du matériel obligatoire</td><td><input type="checkbox" checked={ressources.materiel_obligatoire || false} disabled /></td><td><textarea defaultValue={ressources.materiel_obligatoire_comment || ''} disabled /></td></tr>
            <tr><td>Médiagraphie</td><td><input type="checkbox" checked={ressources.mediagraphie || false} disabled /></td><td><textarea defaultValue={ressources.mediagraphie_comment || ''} disabled /></td></tr>
          </tbody>
        </table>

        <h3>9. Épreuve certificative</h3>
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td>Titre de l'évaluation</td><td><input type="checkbox" checked={epreuve.titre || false} disabled /></td><td><textarea defaultValue={epreuve.titre_comment || ''} disabled /></td></tr>
            <tr><td>Tâches</td><td><input type="checkbox" checked={epreuve.taches || false} disabled /></td><td><textarea defaultValue={epreuve.taches_comment || ''} disabled /></td></tr>
            <tr><td>Contexte de réalisation</td><td><input type="checkbox" checked={epreuve.contexte || false} disabled /></td><td><textarea defaultValue={epreuve.contexte_comment || ''} disabled /></td></tr>
            <tr><td>Pondération (≥ 40%)</td><td><input type="checkbox" checked={epreuve.ponderation_40 || false} disabled /></td><td><textarea defaultValue={epreuve.ponderation_40_comment || ''} disabled /></td></tr>
            <tr><td>Semaine de l'évaluation</td><td><input type="checkbox" checked={epreuve.semaine || false} disabled /></td><td><textarea defaultValue={epreuve.semaine_comment || ''} disabled /></td></tr>
            <tr><td>Grille de correction (analytique et descriptive)</td><td><input type="checkbox" checked={epreuve.grille_correction || false} disabled /></td><td><textarea defaultValue={epreuve.grille_correction_comment || ''} disabled /></td></tr>
          </tbody>
        </table>

        <h2>Partie 2 – Appréciation qualitative</h2>
        <h3>1. Stratégies d'apprentissage</h3>
        <table className="rubric">
          <colgroup>
            <col className="critere" />
            <col className="guide" />
            <col className="choix" />
            <col className="comment" />
          </colgroup>
          <thead><tr><th>Critère</th><th>Guide</th><th>Choix</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr>
              <td>Emploi d'approches issues de la pédagogie active</td>
              <GuideCell
                critere="Emploi d'approches issues de la pédagogie active"
                selectedLevel={levels['Emploi d\'approches issues de la pédagogie active']}
              />
              <td>
                <select value={levels['Emploi d\'approches issues de la pédagogie active'] || ''} disabled>
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea defaultValue={rubric.strategies?.pedagogie_active?.comment || ''} disabled /></td>
            </tr>
            <tr>
              <td>Approches adaptées à la matière du cours</td>
              <GuideCell
                critere="Approches adaptées à la matière du cours"
                selectedLevel={levels['Approches adaptées à la matière du cours']}
              />
              <td>
                <select value={levels['Approches adaptées à la matière du cours'] || ''} disabled>
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea defaultValue={rubric.strategies?.adaptation_discipline?.comment || ''} disabled /></td>
            </tr>
            <tr>
              <td>Alignement des stratégies d'enseignement avec le niveau taxonomique prescrit</td>
              <GuideCell
                critere="Alignement des stratégies d'enseignement avec le niveau taxonomique prescrit"
                selectedLevel={levels['Alignement des stratégies d\'enseignement avec le niveau taxonomique prescrit']}
              />
              <td>
                <select value={levels['Alignement des stratégies d\'enseignement avec le niveau taxonomique prescrit'] || ''} disabled>
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea defaultValue={rubric.strategies?.alignement_taxonomique?.comment || ''} disabled /></td>
            </tr>
            <tr>
              <td>Intégration des technologies éducatives</td>
              <GuideCell
                critere="Intégration des technologies éducatives"
                selectedLevel={levels['Intégration des technologies éducatives']}
              />
              <td>
                <select value={levels['Intégration des technologies éducatives'] || ''} disabled>
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea defaultValue={rubric.strategies?.techno_educatives?.comment || ''} disabled /></td>
            </tr>
          </tbody>
        </table>

        <h3>2. Calendrier des activités d'apprentissage, d'enseignement et d'évaluation</h3>
        <table className="rubric">
          <colgroup>
            <col className="critere" />
            <col className="guide" />
            <col className="choix" />
            <col className="comment" />
          </colgroup>
          <thead><tr><th>Critère</th><th>Guide</th><th>Choix</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr>
              <td>Présence d'évaluations formatives en classe</td>
              <GuideCell
                critere="Présence d'évaluations formatives en classe"
                selectedLevel={levels['Présence d\'évaluations formatives en classe']}
              />
              <td>
                <select value={levels['Présence d\'évaluations formatives en classe'] || ''} disabled>
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea defaultValue={rubric.calendrier?.formatives_classe?.comment || ''} disabled /></td>
            </tr>
            <tr>
              <td>Présence de lectures ou de travaux hors classe</td>
              <GuideCell
                critere="Présence de lectures ou de travaux hors classe"
                selectedLevel={levels['Présence de lectures ou de travaux hors classe']}
              />
              <td>
                <select value={levels['Présence de lectures ou de travaux hors classe'] || ''} disabled>
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea defaultValue={rubric.calendrier?.hors_classe?.comment || ''} disabled /></td>
            </tr>
          </tbody>
        </table>

        <h3>3. Évaluation des apprentissages</h3>
        <table className="rubric">
          <colgroup>
            <col className="critere" />
            <col className="guide" />
            <col className="choix" />
            <col className="comment" />
          </colgroup>
          <thead><tr><th>Critère</th><th>Guide</th><th>Choix</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr>
              <td>Évaluation de la totalité des compétences du cours</td>
              <GuideCell
                critere="Évaluation de la totalité des compétences du cours"
                selectedLevel={levels['Évaluation de la totalité des compétences du cours']}
              />
              <td>
                <select value={levels['Évaluation de la totalité des compétences du cours'] || ''} disabled>
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea defaultValue={rubric.eval_apprentissages?.totalite_competences?.comment || ''} disabled /></td>
            </tr>
            <tr>
              <td>Distribution équilibrée des évaluations sommatives</td>
              <GuideCell
                critere="Distribution équilibrée des évaluations sommatives"
                selectedLevel={levels['Distribution équilibrée des évaluations sommatives']}
              />
              <td>
                <select value={levels['Distribution équilibrée des évaluations sommatives'] || ''} disabled>
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea defaultValue={rubric.eval_apprentissages?.distribution_equilibree?.comment || ''} disabled /></td>
            </tr>
          </tbody>
        </table>

        <h3>4. Épreuve certificative (EC) ou Épreuve synthèse de programme (ESP)</h3>
        <p className="hint">Si celle-ci n'est pas déjà donnée dans le plan-cadre*</p>
        <table className="rubric">
          <colgroup>
            <col className="critere" />
            <col className="guide" />
            <col className="choix" />
            <col className="comment" />
          </colgroup>
          <thead><tr><th>Critère</th><th>Guide</th><th>Choix</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr>
              <td>Concordance de l'évaluation certificative avec le niveau taxonomique du cours</td>
              <GuideCell
                critere="Concordance de l'évaluation certificative avec le niveau taxonomique du cours"
                selectedLevel={levels['Concordance de l\'évaluation certificative avec le niveau taxonomique du cours']}
              />
              <td>
                <select value={levels['Concordance de l\'évaluation certificative avec le niveau taxonomique du cours'] || ''} disabled>
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea defaultValue={rubric.epreuve?.concordance_taxonomique?.comment || ''} disabled /></td>
            </tr>
            <tr>
              <td>Préparation à l'évaluation certificative</td>
              <GuideCell
                critere="Préparation à l'évaluation certificative"
                selectedLevel={levels['Préparation à l\'évaluation certificative']}
              />
              <td>
                <select value={levels['Préparation à l\'évaluation certificative'] || ''} disabled>
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea defaultValue={rubric.epreuve?.preparation?.comment || ''} disabled /></td>
            </tr>
            <tr>
              <td>Caractère authentique et complexe de l'évaluation certificative</td>
              <GuideCell
                critere="Caractère authentique et complexe de l'évaluation certificative"
                selectedLevel={levels['Caractère authentique et complexe de l\'évaluation certificative']}
              />
              <td>
                <select value={levels['Caractère authentique et complexe de l\'évaluation certificative'] || ''} disabled>
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea defaultValue={rubric.epreuve?.authentique_complexe?.comment || ''} disabled /></td>
            </tr>
          </tbody>
        </table>
      </form>
    </section>
  );
}
